import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fmtMoney, fmtNumber, imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { FinancingCalculator } from "@/components/FinancingCalculator";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ChevronLeft, ChevronRight, X, ZoomIn, Gauge, Fuel, Cog, MapPin, Calendar,
  Palette, Users, DoorOpen, Zap, CheckCircle2, ArrowLeft,
} from "lucide-react";

export default function VehicleDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [v, setV] = useState(null);
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [zoom, setZoom] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/vehicles/${id}`).then((r) => setV(r.data)).catch(() => navigate("/inventory"));
  }, [id, navigate]);

  if (!v) return <Layout><div className="flex min-h-[60vh] items-center justify-center"><div className="h-10 w-10 animate-spin rounded-full border-2 border-gold border-t-transparent" /></div></Layout>;

  const images = v.images?.length ? v.images : [{ url: "" }];
  const vehicleName = `${v.year} ${v.make} ${v.model}`;
  const next = () => setActive((a) => (a + 1) % images.length);
  const prev = () => setActive((a) => (a - 1 + images.length) % images.length);

  const startApplication = async (finOverride) => {
    if (!user) { login(); return; }
    try {
      const res = await api.post("/applications", { vehicle_id: v.id, financing: finOverride || {} });
      navigate(`/apply/${res.data.id}`);
    } catch (e) {
      navigate("/dashboard");
    }
  };

  const SPECS = [
    ["VIN", v.vin, null], ["Year", v.year, Calendar], ["Mileage", `${fmtNumber(v.mileage)} mi`, Gauge],
    ["Engine", v.engine, Zap], ["Displacement", v.displacement, null], ["Horsepower", `${v.horsepower} hp`, null],
    ["Torque", v.torque, null], ["Transmission", v.transmission, Cog], ["Drivetrain", v.drivetrain, null],
    ["Fuel Type", v.fuel_type, Fuel], ["MPG", v.mpg, null], ["Exterior Color", v.exterior_color, Palette],
    ["Interior Color", v.interior_color, null], ["Body Type", v.body_type, null], ["Seats", v.seats, Users],
    ["Doors", v.doors, DoorOpen], ["Condition", v.condition, null], ["Stock #", v.stock_number, null],
    ["Location", v.location, MapPin],
  ];

  return (
    <Layout vehicleName={vehicleName}>
      <div className="container-x py-8">
        <button onClick={() => navigate("/inventory")} className="mb-6 inline-flex items-center gap-1 text-sm text-slate-600 hover:text-gold" data-testid="back-to-inventory">
          <ArrowLeft className="h-4 w-4" /> Back to Inventory
        </button>

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-5">
          {/* Gallery */}
          <div className="lg:col-span-3">
            <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-[#FFFFFF]">
              {v.is_demo && <span className="absolute left-4 top-4 z-10 rounded-full border border-white/15 bg-black/60 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-700 backdrop-blur">Demo Data</span>}
              <div className="relative aspect-[16/10] cursor-zoom-in" onClick={() => setLightbox(true)} data-testid="gallery-main-image">
                <img src={imgUrl(images[active].url)} alt={vehicleName} className="h-full w-full object-cover" />
                <div className="absolute bottom-4 right-4 rounded-full bg-black/60 p-2 text-slate-900 backdrop-blur"><ZoomIn className="h-4 w-4" /></div>
              </div>
              <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-slate-900 backdrop-blur hover:bg-black/70" data-testid="gallery-prev"><ChevronLeft className="h-5 w-5" /></button>
              <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-slate-900 backdrop-blur hover:bg-black/70" data-testid="gallery-next"><ChevronRight className="h-5 w-5" /></button>
            </div>
            <div className="mt-3 flex gap-3 overflow-x-auto no-scrollbar">
              {images.map((img, i) => (
                <button key={i} onClick={() => setActive(i)} data-testid={`gallery-thumb-${i}`}
                  className={`relative h-20 w-28 flex-shrink-0 overflow-hidden rounded-lg border-2 transition-colors ${active === i ? "border-gold" : "border-transparent opacity-60 hover:opacity-100"}`}>
                  <img src={imgUrl(img.url)} alt={img.type} className="h-full w-full object-cover" />
                  <span className="absolute inset-x-0 bottom-0 bg-black/60 py-0.5 text-center text-[9px] capitalize text-slate-900">{img.type}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Summary */}
          <div className="lg:col-span-2">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">{v.body_type} · {v.condition}</p>
            <h1 className="mt-2 font-display text-3xl font-extrabold text-slate-900 lg:text-4xl">{v.make} {v.model}</h1>
            <p className="text-lg text-slate-600">{v.year} · {v.trim}</p>
            <div className="mt-6 flex items-end gap-3">
              <span className="font-display text-4xl font-extrabold text-gold">{fmtMoney(v.price)}</span>
              <span className={`mb-1.5 rounded-full border px-3 py-1 text-xs font-semibold capitalize ${v.status === "available" ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-400" : v.status === "reserved" ? "border-amber-500/30 bg-amber-500/15 text-amber-400" : "border-red-500/30 bg-red-500/15 text-red-400"}`}>{v.status}</span>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              {[[Gauge, `${fmtNumber(v.mileage)} mi`], [Cog, v.transmission], [Fuel, v.fuel_type], [MapPin, v.location]].map(([Icon, val], i) => (
                <div key={i} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-[#FFFFFF] px-3 py-2.5 text-sm text-slate-700">
                  <Icon className="h-4 w-4 text-gold" /> {val}
                </div>
              ))}
            </div>

            <Button onClick={() => startApplication()} disabled={v.status === "sold"} data-testid="start-application-btn" className="btn-gold mt-6 w-full rounded-full py-6 text-base">
              {v.status === "sold" ? "Vehicle Sold" : "Start Financing Application"}
            </Button>
            <p className="mt-3 text-center text-xs text-slate-500">Secure sign-in required to apply. Takes about 5 minutes.</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <Tabs defaultValue="specs">
              <TabsList className="bg-[#FFFFFF]">
                <TabsTrigger value="specs" data-testid="tab-specs" className="data-[state=active]:bg-gold data-[state=active]:text-[#EEF3FC]">Specifications</TabsTrigger>
                <TabsTrigger value="features" data-testid="tab-features" className="data-[state=active]:bg-gold data-[state=active]:text-[#EEF3FC]">Features</TabsTrigger>
                <TabsTrigger value="desc" data-testid="tab-desc" className="data-[state=active]:bg-gold data-[state=active]:text-[#EEF3FC]">Description</TabsTrigger>
              </TabsList>
              <TabsContent value="specs" className="mt-5">
                <div className="grid grid-cols-1 gap-x-8 gap-y-0 sm:grid-cols-2">
                  {SPECS.map(([label, val]) => (
                    <div key={label} className="flex items-center justify-between border-b border-slate-200 py-3 text-sm">
                      <span className="text-slate-600">{label}</span>
                      <span className="font-medium text-slate-900">{val}</span>
                    </div>
                  ))}
                </div>
              </TabsContent>
              <TabsContent value="features" className="mt-5">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {v.features?.map((f) => (
                    <div key={f} className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle2 className="h-4 w-4 text-gold" /> {f}</div>
                  ))}
                </div>
              </TabsContent>
              <TabsContent value="desc" className="mt-5">
                <p className="text-sm leading-relaxed text-slate-700">{v.description}</p>
              </TabsContent>
            </Tabs>
          </div>
          <div className="lg:col-span-2">
            <FinancingCalculator initialPrice={v.price} vehicleName={vehicleName} onApply={startApplication} compact />
          </div>
        </div>
      </div>

      {/* Lightbox */}
      <Dialog open={lightbox} onOpenChange={setLightbox}>
        <DialogContent className="max-w-5xl border-slate-200 bg-[#EEF3FC] p-0">
          <div className="relative">
            <img src={imgUrl(images[active].url)} alt="" onClick={() => setZoom(!zoom)} className={`w-full rounded-lg transition-transform duration-300 ${zoom ? "scale-150 cursor-zoom-out" : "cursor-zoom-in"}`} />
            <button onClick={() => setLightbox(false)} className="absolute right-3 top-3 rounded-full bg-black/60 p-2 text-slate-900 hover:bg-black/80"><X className="h-5 w-5" /></button>
            <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-2 text-slate-900"><ChevronLeft className="h-6 w-6" /></button>
            <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-2 text-slate-900"><ChevronRight className="h-6 w-6" /></button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
